import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Collapse,
  TextField,
  Theme,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import {
  ExpandMore,
  ExpandLess,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  RadioButtonChecked,
  RadioButtonUnchecked,
  AssignmentTurnedIn,
  Visibility,
  Lock as LockIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
} from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, ContentState, Modifier, CompositeDecorator, ContentBlock, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import {
  FormType,
  AvailableFormField,
  FormAdminField,
  AdminFieldType,
  FormWizardProgram,
  FormWizardActivity,
  EntityType,
} from './form-responses-types';
import { FormNotificationSettings } from './FormNotificationSettings';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_TASKS_FOR_FIELD_MAPPING } from '../forms.gql';
import {
  applicantTaskMappingKey,
  isMappableTaskFieldType,
  canFillTarget,
  APPLICANT_IDENTITY_TARGET_TYPES,
} from './applicant-task-prefill';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    settingsSection: {
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '20px 24px',
      marginBottom: '16px',
    },
    settingsSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
    },
    settingsSectionHeaderExpanded: {
      paddingBottom: '16px',
      borderBottom: '1px solid #efefef',
      marginBottom: '16px',
    },
    settingsSectionInfo: {
      flex: 1,
    },
    settingsSectionTitle: {
      fontSize: '16px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    settingsSectionDescription: {
      fontSize: '14px',
      color: '#808080',
      lineHeight: '1.4',
      marginTop: '4px',
    },
    settingsExpandIcon: {
      padding: '4px',
      color: '#808080',
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    responseCard: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '16px',
    },
    entityCard: {
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      backgroundColor: '#fff',
      marginBottom: '12px',
      overflow: 'hidden',
    },
    entityCardHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.01)',
      },
    },
    entityCardIconBox: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    entityCardInfo: {
      flex: 1,
      marginLeft: '12px',
    },
    entityCardTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: theme.color.grey.neutral400,
    },
    entityCardDescription: {
      fontSize: '0.8rem',
      color: theme.color.grey.neutral300,
    },
    entityCardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginLeft: '12px',
    },
    entityCardBody: {
      padding: '16px 20px 20px 20px',
      borderTop: '1px solid #efefef',
    },
  })
);

interface SettingsTabProps {
  formId: string;
  form: FormType;
  fieldMappings: Record<string, string>;
  availableFormFields: AvailableFormField[];
  programs: FormWizardProgram[];
  allActivities: FormWizardActivity[];
  activitiesForProgram: FormWizardActivity[];
  sessionsForActivity: any[];
  genericSessionCount: number;
  savingMappings: boolean;
  adminFields: FormAdminField[];
  savingAdminFields: boolean;
  onSaveAdminFields: (
    fields: Array<{ adminFieldId: string | null; name: string; type: string; order: number }>
  ) => void;
  onFieldMappingsChange: (mappings: Record<string, string>) => void;
  onSaveFieldMappings: () => void;
  onGenericSessionCountChange: (count: number | ((prev: number) => number)) => void;
  onRefetchForms: () => void;
}

interface DraftAdminField {
  adminFieldId: string | null;
  name: string;
  type: AdminFieldType;
}

const ADMIN_FIELD_TYPE_OPTIONS: Array<{ value: AdminFieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

const activityFields = [
  { key: 'activity.activityName', label: 'Activity Name', type: 'string' },
  { key: 'activity.activityDescription', label: 'Activity Description', type: 'string' },
  { key: 'activity.startDate', label: 'Start Date', type: 'date' },
  { key: 'activity.endDate', label: 'End Date', type: 'date' },
  { key: 'activity.location.formatted', label: 'Location', type: 'string' },
  { key: 'activity.remoteLocation.details', label: 'Remote Location Details', type: 'string' },
  { key: 'activity.maxTeam', label: 'Max Team Size', type: 'number' },
];

const rosterTemplateFields = [
  { key: 'rosterTemplate.date', label: 'Date', type: 'date' },
  { key: 'rosterTemplate.location.formatted', label: 'Location', type: 'string' },
];

const applicantFields = [
  { key: 'applicant.email', label: 'Email (required)', type: 'string' },
  { key: 'applicant.firstName', label: 'First Name (required)', type: 'string' },
  { key: 'applicant.lastName', label: 'Last Name (required)', type: 'string' },
  { key: 'applicant.contactNumber', label: 'Contact Number', type: 'string' },
  { key: 'applicant.dob', label: 'Date of Birth', type: 'date' },
];

const calendarInviteFields = [
  { key: 'calendarInvite.attendeeEmail', label: 'Attendee Email', type: 'string' },
  { key: 'calendarInvite.date', label: 'Date', type: 'date' },
  { key: 'calendarInvite.startTime', label: 'Start Time', type: 'string' },
  { key: 'calendarInvite.endTime', label: 'End Time', type: 'string' },
  { key: 'calendarInvite.location.formatted', label: 'Location', type: 'string' },
  { key: 'calendarInvite.meetingLink', label: 'Meeting Link', type: 'string' },
  { key: 'calendarInvite.phoneNumber', label: 'Phone Number', type: 'string' },
];

const ENTITY_TYPE_CONFIGS: Array<{
  type: EntityType;
  title: string;
  description: string;
  svgPath: string;
}> = [
  {
    type: 'activity',
    title: 'Activity',
    description: 'Create a new volunteer activity from this submission',
    svgPath:
      'M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z',
  },
  {
    type: 'roster_template',
    title: 'Roster Template',
    description: 'Create a roster template for scheduling volunteers',
    svgPath:
      'M19 3H14.82C14.4 1.84 13.3 1 12 1C10.7 1 9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 3C12.55 3 13 3.45 13 4C13 4.55 12.55 5 12 5C11.45 5 11 4.55 11 4C11 3.45 11.45 3 12 3ZM14 17H7V15H14V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z',
  },
  {
    type: 'email',
    title: 'Email',
    description: 'Send an email based on this submission',
    svgPath:
      'M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z',
  },
  {
    type: 'calendar_invite',
    title: 'Calendar Invite',
    description: 'Create a calendar event from this submission',
    svgPath:
      'M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z',
  },
  {
    type: 'applicant',
    title: 'Applicant',
    description: 'Create an applicant and start their onboarding from this submission',
    svgPath:
      'M15 12C17.21 12 19 10.21 19 8C19 5.79 17.21 4 15 4C12.79 4 11 5.79 11 8C11 10.21 12.79 12 15 12ZM6 10V7H4V10H1V12H4V15H6V12H9V10H6ZM15 14C12.33 14 7 15.34 7 18V20H23V18C23 15.34 17.67 14 15 14Z',
  },
];

const RosterNotesTemplateEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  availableFormFields: AvailableFormField[];
  label?: string;
  placeholder?: string;
}> = ({
  value,
  onChange,
  availableFormFields,
  label = 'Roster Notes Template',
  placeholder = 'Compose roster notes template with merge tags...',
}) => {
  const { theme } = useCampfireTheme();
  const fieldsRef = useRef<AvailableFormField[]>(availableFormFields);
  fieldsRef.current = availableFormFields;

  const [decorator] = useState(() => {
    const mergeTagStrategy = (contentBlock: ContentBlock, callback: (start: number, end: number) => void) => {
      const text = contentBlock.getText();
      const regex = /\{\{[^}]+\}\}/g;
      let match = regex.exec(text);
      while (match !== null) {
        callback(match.index, match.index + match[0].length);
        match = regex.exec(text);
      }
    };

    const MergeTagChip = (props: any) => {
      const { decoratedText, children } = props;
      const fieldId = (decoratedText || '').replace(/[{}]/g, '').trim();
      const fieldName = fieldsRef.current.find((f) => f.taskFieldId === fieldId)?.name || fieldId;
      return (
        <span
          style={{
            position: 'relative',
            backgroundColor: '#e3f2fd',
            borderRadius: 4,
            display: 'inline',
          }}
        >
          <span style={{ fontSize: 0, lineHeight: 0, width: 0, overflow: 'hidden', display: 'inline-block' }}>
            {children}
          </span>
          <span
            contentEditable={false}
            suppressContentEditableWarning
            style={{
              padding: '2px 6px',
              color: '#1565c0',
              fontWeight: 500,
              userSelect: 'none',
              fontSize: '0.85em',
            }}
          >
            {fieldName}
          </span>
        </span>
      );
    };

    return new CompositeDecorator([{ strategy: mergeTagStrategy, component: MergeTagChip }]);
  });

  const [editorState, setEditorState] = useState(() => {
    if (value && (value.includes('{{') || value.startsWith('<'))) {
      try {
        const blocksFromHTML = convertFromHTML(value);
        if (blocksFromHTML.contentBlocks) {
          const content = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
          return EditorState.createWithContent(content, decorator);
        }
      } catch {
        // Fall through to empty state
      }
    }
    return EditorState.createEmpty(decorator);
  });

  const hydratedFromPropRef = useRef(Boolean(value));
  useEffect(() => {
    if (hydratedFromPropRef.current) return;
    if (!value || (!value.includes('{{') && !value.startsWith('<'))) return;
    try {
      const blocksFromHTML = convertFromHTML(value);
      if (blocksFromHTML.contentBlocks) {
        const content = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
        setEditorState(EditorState.createWithContent(content, decorator));
        hydratedFromPropRef.current = true;
      }
    } catch {
      // ignore
    }
  }, [value, decorator]);

  const handleEditorChange = (newState: EditorState) => {
    setEditorState(newState);
    const hasContent = newState.getCurrentContent().hasText();
    const html = hasContent ? stateToHTML(newState.getCurrentContent()) : '';
    onChange(html);
  };

  const insertFieldTag = (taskFieldId: string) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const newContent = Modifier.insertText(contentState, selectionState, `{{${taskFieldId}}}`);
    const newState = EditorState.push(editorState, newContent, 'insert-characters');
    handleEditorChange(newState);
  };

  return (
    <Box style={{ marginTop: 16 }}>
      <Box display='flex' alignItems='center' style={{ marginBottom: 8 }}>
        <Typography style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
          {label}
        </Typography>
        <FormControl size='small' variant='outlined' style={{ marginLeft: 'auto', minWidth: 160 }}>
          <Select
            displayEmpty
            value=''
            onChange={(e) => {
              if (e.target.value) insertFieldTag(String(e.target.value));
            }}
            style={{ fontSize: '0.8rem' }}
          >
            <MenuItem value='' disabled>
              Insert Field...
            </MenuItem>
            {availableFormFields.map((f) => (
              <MenuItem key={f.taskFieldId} value={f.taskFieldId}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          padding: '8px 12px',
          minHeight: 80,
        }}
      >
        <CampfireRichEditor editorState={editorState} setEditorState={handleEditorChange} placeholder={placeholder} />
      </Box>
      <Typography style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
        Use the &quot;Insert Field&quot; dropdown to add form field values. Format text using the toolbar.
      </Typography>
    </Box>
  );
};

const SessionMappingRow: React.FC<{
  sessionKey: string;
  sessionLabel: string;
  fieldMappings: Record<string, string>;
  availableFormFields: AvailableFormField[];
  onFieldMappingsChange: (mappings: Record<string, string>) => void;
  onRemove?: () => void;
}> = ({ sessionKey, sessionLabel, fieldMappings, availableFormFields, onFieldMappingsChange, onRemove }) => {
  const { theme } = useCampfireTheme();

  return (
    <Box style={{ marginBottom: '24px' }}>
      <Box display='flex' alignItems='center' style={{ marginBottom: '12px' }}>
        <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
          {sessionLabel}
        </Typography>
        {onRemove && (
          <TabletButton
            size='small'
            variant='outlined'
            style={{
              marginLeft: '8px',
              textTransform: 'none',
              padding: '4px 12px',
              borderColor: theme.color.grey.neutral200,
              color: theme.color.grey.neutral400,
            }}
            onClick={onRemove}
          >
            Remove
          </TabletButton>
        )}
      </Box>
      <Grid container spacing={2}>
        {['customName', 'startTime', 'endTime'].map((fieldName) => (
          <Grid item xs={12} sm={4} key={fieldName}>
            <FormControl fullWidth variant='outlined' size='small'>
              <InputLabel>
                {fieldName === 'customName' ? 'Custom Name' : fieldName === 'startTime' ? 'Start Time' : 'End Time'}
              </InputLabel>
              <Select
                value={fieldMappings[`${sessionKey}.${fieldName}`] || ''}
                onChange={(e) =>
                  onFieldMappingsChange({
                    ...fieldMappings,
                    [`${sessionKey}.${fieldName}`]: String(e.target.value),
                  })
                }
                label={
                  fieldName === 'customName' ? 'Custom Name' : fieldName === 'startTime' ? 'Start Time' : 'End Time'
                }
              >
                <MenuItem value=''>
                  <em>No mapping</em>
                </MenuItem>
                {availableFormFields.map((formField) => (
                  <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                    {formField.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

type RoutingField = { key: string; label: string; helperText: string };

const DEFAULT_ROUTING_FIELDS: RoutingField[] = [
  {
    key: 'replyTo',
    label: 'Reply to',
    helperText: 'Where replies go. Leave blank to use your organisation default.',
  },
  { key: 'cc', label: 'Cc', helperText: 'Separate multiple addresses with commas' },
  { key: 'bcc', label: 'Bcc', helperText: 'Separate multiple addresses with commas' },
];

const CALENDAR_INVITE_ROUTING_FIELDS: RoutingField[] = [
  {
    key: 'attendees',
    label: 'Always invite',
    helperText: 'Added as attendees on every invite, so the event lands on their calendar',
  },
  { key: 'cc', label: 'Cc', helperText: 'Emailed a copy only, not added to the event' },
  { key: 'bcc', label: 'Bcc', helperText: 'Emailed a hidden copy only, not added to the event' },
];

const EmailRoutingFields: React.FC<{
  keyPrefix: string;
  fieldMappings: Record<string, string>;
  onFieldMappingsChange: (mappings: Record<string, string>) => void;
  fields?: RoutingField[];
}> = ({ keyPrefix, fieldMappings, onFieldMappingsChange, fields }) => {
  const routingFields = fields ?? DEFAULT_ROUTING_FIELDS;

  return (
    <Grid container spacing={2} style={{ marginTop: '4px' }}>
      {routingFields.map(({ key, label, helperText }) => (
        <Grid item xs={12} sm={4} key={key}>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            label={label}
            placeholder='name@example.org'
            helperText={helperText}
            value={fieldMappings[`${keyPrefix}.${key}`] || ''}
            onChange={(e) => onFieldMappingsChange({ ...fieldMappings, [`${keyPrefix}.${key}`]: e.target.value })}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const TemplatedTextInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  availableFormFields: AvailableFormField[];
  label: string;
  placeholder?: string;
}> = ({ value, onChange, availableFormFields, label, placeholder }) => {
  const { theme } = useCampfireTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const insertFieldTag = (taskFieldId: string) => {
    const tag = `{{${taskFieldId}}}`;
    const input = inputRef.current;
    if (!input) {
      onChange(`${value}${tag}`);
      return;
    }
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const next = value.slice(0, start) + tag + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + tag.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <Box style={{ marginBottom: 16 }}>
      <Box display='flex' alignItems='center' style={{ marginBottom: 8 }}>
        <Typography style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
          {label}
        </Typography>
        <FormControl size='small' variant='outlined' style={{ marginLeft: 'auto', minWidth: 160 }}>
          <Select
            displayEmpty
            value=''
            onChange={(e) => {
              if (e.target.value) insertFieldTag(String(e.target.value));
            }}
            style={{ fontSize: '0.8rem' }}
          >
            <MenuItem value='' disabled>
              Insert Field...
            </MenuItem>
            {availableFormFields.map((f) => (
              <MenuItem key={f.taskFieldId} value={f.taskFieldId}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TextField
        fullWidth
        variant='outlined'
        size='small'
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <Typography style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
        Use the &quot;Insert Field&quot; dropdown to add merge tags. Type freely to mix text and dynamic values.
      </Typography>
    </Box>
  );
};

export const SettingsTab: React.FC<SettingsTabProps> = ({
  formId,
  form,
  fieldMappings,
  availableFormFields,
  programs,
  activitiesForProgram,
  sessionsForActivity,
  genericSessionCount,
  savingMappings,
  adminFields,
  savingAdminFields,
  onSaveAdminFields,
  onFieldMappingsChange,
  onSaveFieldMappings,
  onGenericSessionCountChange,
  onRefetchForms,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [wizardSettingsExpanded, setWizardSettingsExpanded] = useState(false);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const [approvalWorkflowExpanded, setApprovalWorkflowExpanded] = useState(false);
  const [adminFieldsExpanded, setAdminFieldsExpanded] = useState(false);
  const [individualViewExpanded, setIndividualViewExpanded] = useState(false);
  const [draftAdminFields, setDraftAdminFields] = useState<DraftAdminField[]>([]);

  useEffect(() => {
    setDraftAdminFields(adminFields.map((f) => ({ adminFieldId: f.adminFieldId, name: f.name, type: f.type })));
  }, [adminFields]);

  const updateDraftAdminField = (index: number, changes: Partial<DraftAdminField>) => {
    setDraftAdminFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...changes } : f)));
  };

  const moveDraftAdminField = (index: number, direction: -1 | 1) => {
    setDraftAdminFields((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSaveAdminFieldsClick = () => {
    onSaveAdminFields(
      draftAdminFields
        .filter((f) => f.name.trim() !== '')
        .map((f, index) => ({ adminFieldId: f.adminFieldId, name: f.name.trim(), type: f.type, order: index }))
    );
  };
  const { data: mappingTasksData } = useCampfireQuery<any, undefined>(GET_TASKS_FOR_FIELD_MAPPING);

  const mappableTasks = useMemo(() => {
    const cakes = mappingTasksData?.vm?.cakes ?? [];
    const tasks: Array<{
      taskId: string;
      title: string;
      cakeTitle: string;
      fields: Array<{ taskFieldId: string; name: string; typeName: string }>;
    }> = [];

    cakes.forEach((cake: any) => {
      (cake.allTasks ?? []).forEach((task: any) => {
        if (task.isHidden) return;
        const fields = (task.taskItems ?? [])
          .filter((item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType' && item.field)
          .filter((item: any) => isMappableTaskFieldType(item.field.__typename))
          .map((item: any) => ({
            taskFieldId: item.field.taskFieldId,
            name: item.field.name,
            typeName: item.field.__typename,
          }));

        if (fields.length > 0) {
          tasks.push({ taskId: task.taskId, title: task.title, cakeTitle: cake.title, fields });
        }
      });
    });

    return tasks;
  }, [mappingTasksData]);

  const [expandedCards, setExpandedCards] = useState<Record<EntityType, boolean>>(() =>
    ENTITY_TYPE_CONFIGS.reduce((acc, config) => ({ ...acc, [config.type]: false }), {} as Record<EntityType, boolean>)
  );

  const toggleCard = (entityType: EntityType) => {
    setExpandedCards((prev) => ({ ...prev, [entityType]: !prev[entityType] }));
  };

  const renderEntityMappings = (entityType: EntityType): React.ReactNode => {
    if (availableFormFields.length === 0) {
      return (
        <Typography style={{ fontSize: '0.85rem', color: theme.color.grey.neutral300 }}>
          No form fields available. Add fields to this form to configure mappings.
        </Typography>
      );
    }

    switch (entityType) {
      case 'activity':
        return (
          <>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map form fields to Activity properties. When creating an Activity from the Form Wizard, these values will
              be pre-filled.
            </Typography>
            <Grid container spacing={2}>
              {activityFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControl fullWidth variant='outlined' size='small'>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })}
                      label={field.label}
                    >
                      <MenuItem value=''>
                        <em>No mapping</em>
                      </MenuItem>
                      {availableFormFields.map((formField) => (
                        <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                          {formField.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </>
        );

      case 'applicant':
        return (
          <>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map form fields to the applicant&apos;s details. When creating an applicant from the Form Wizard, these
              values are pre-filled and can be edited before the record is created.
            </Typography>
            <Grid container spacing={2}>
              {applicantFields.map((field) => {
                const targetType = APPLICANT_IDENTITY_TARGET_TYPES[field.key];
                const compatible = availableFormFields.filter((formField) =>
                  canFillTarget(targetType, formField.fieldType)
                );
                return (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <FormControl fullWidth variant='outlined' size='small'>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        value={fieldMappings[field.key] || ''}
                        onChange={(e) =>
                          onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })
                        }
                        label={field.label}
                      >
                        <MenuItem value=''>
                          <em>No mapping</em>
                        </MenuItem>
                        {compatible.map((formField) => (
                          <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                            {formField.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {compatible.length === 0 && (
                        <Typography
                          style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300, marginTop: '4px' }}
                        >
                          No form field of a matching type
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                );
              })}
            </Grid>

            <Typography className={classes.sectionTitle} style={{ marginTop: '28px', marginBottom: '4px' }}>
              Onboarding task fields
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Carry answers straight onto the applicant&apos;s onboarding tasks, so a coordinator does not re-key what
              the form already asked. Attachments, signatures, addresses and matrices cannot be carried across.
            </Typography>
            {mappableTasks.length === 0 ? (
              <Typography style={{ fontSize: '0.85rem', color: theme.color.grey.neutral300 }}>
                No onboarding tasks with mappable fields.
              </Typography>
            ) : (
              mappableTasks.map((task) => (
                <Box key={task.taskId} style={{ marginBottom: '20px' }}>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: theme.color.grey.neutral400,
                      marginBottom: '8px',
                    }}
                  >
                    {task.title}
                    <span
                      style={{ fontWeight: 400, color: theme.color.grey.neutral300 }}
                    >{` · ${task.cakeTitle}`}</span>
                  </Typography>
                  <Grid container spacing={2}>
                    {task.fields.map((taskField) => {
                      const key = applicantTaskMappingKey(task.taskId, taskField.taskFieldId);
                      const compatible = availableFormFields.filter((formField) =>
                        canFillTarget(taskField.typeName, formField.fieldType)
                      );
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <FormControl fullWidth variant='outlined' size='small'>
                            <InputLabel>{taskField.name}</InputLabel>
                            <Select
                              value={fieldMappings[key] || ''}
                              onChange={(e) =>
                                onFieldMappingsChange({ ...fieldMappings, [key]: String(e.target.value) })
                              }
                              label={taskField.name}
                              disabled={compatible.length === 0}
                            >
                              <MenuItem value=''>
                                <em>No mapping</em>
                              </MenuItem>
                              {compatible.map((formField) => (
                                <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                                  {formField.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))
            )}
          </>
        );

      case 'roster_template':
        return (
          <>
            <Typography className={classes.sectionTitle} style={{ marginBottom: '12px' }}>
              Default Program &amp; Activity
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Select default program and activity for roster templates created from this form.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant='outlined' size='small'>
                  <InputLabel>Default Program</InputLabel>
                  <Select
                    value={fieldMappings['rosterTemplate.defaultProgramId'] || ''}
                    onChange={(e) => {
                      const newProgramId = String(e.target.value);
                      onFieldMappingsChange({
                        ...fieldMappings,
                        'rosterTemplate.defaultProgramId': newProgramId,
                        'rosterTemplate.defaultActivityId': '',
                      });
                    }}
                    label='Default Program'
                  >
                    <MenuItem value=''>
                      <em>No default</em>
                    </MenuItem>
                    {programs.map((program) => (
                      <MenuItem key={program.programId} value={program.programId}>
                        {program.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant='outlined' size='small'>
                  <InputLabel>Default Activity</InputLabel>
                  <Select
                    value={fieldMappings['rosterTemplate.defaultActivityId'] || ''}
                    onChange={(e) =>
                      onFieldMappingsChange({
                        ...fieldMappings,
                        'rosterTemplate.defaultActivityId': String(e.target.value),
                      })
                    }
                    label='Default Activity'
                  >
                    <MenuItem value=''>
                      <em>No default</em>
                    </MenuItem>
                    {activitiesForProgram.map((activity) => (
                      <MenuItem key={activity.activityId} value={activity.activityId}>
                        {activity.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <Typography className={classes.sectionTitle} style={{ marginBottom: '12px' }}>
              Roster Template Field Mappings
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map form fields to Roster Template properties. When creating a Roster Template from the Form Wizard, these
              values will be pre-filled.
            </Typography>
            <TemplatedTextInput
              value={fieldMappings['rosterTemplate.rosterName'] || ''}
              onChange={(v) => onFieldMappingsChange({ ...fieldMappings, 'rosterTemplate.rosterName': v })}
              availableFormFields={availableFormFields}
              label='Custom Roster Name'
              placeholder='e.g. Application #{{__submissionNumber}}'
            />
            <Grid container spacing={2}>
              {rosterTemplateFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControl fullWidth variant='outlined' size='small'>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })}
                      label={field.label}
                    >
                      <MenuItem value=''>
                        <em>No mapping</em>
                      </MenuItem>
                      {availableFormFields.map((formField) => (
                        <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                          {formField.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
            <RosterNotesTemplateEditor
              value={fieldMappings['rosterTemplate.rosterNotes'] || ''}
              onChange={(html) => onFieldMappingsChange({ ...fieldMappings, 'rosterTemplate.rosterNotes': html })}
              availableFormFields={availableFormFields}
            />

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <Typography className={classes.sectionTitle} style={{ marginBottom: '12px' }}>
              Session Customization Mappings
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              {sessionsForActivity.length > 0
                ? 'Map form fields to session properties for the selected activity.'
                : "Map form fields to session properties by position. When creating a roster, these mappings will apply to the activity's sessions in order (1st mapping \u2192 1st session, etc)."}
            </Typography>

            {sessionsForActivity.length > 0 &&
              sessionsForActivity.map((session: any) => (
                <SessionMappingRow
                  key={session.sessionId}
                  sessionKey={`session.${session.sessionId}`}
                  sessionLabel={session.name}
                  fieldMappings={fieldMappings}
                  availableFormFields={availableFormFields}
                  onFieldMappingsChange={onFieldMappingsChange}
                />
              ))}

            {sessionsForActivity.length === 0 && (
              <>
                {Array.from({ length: genericSessionCount }, (_unused, i) => i).map((sessionIndex) => (
                  <SessionMappingRow
                    key={sessionIndex}
                    sessionKey={`session.${sessionIndex}`}
                    sessionLabel={`Session ${sessionIndex + 1}`}
                    fieldMappings={fieldMappings}
                    availableFormFields={availableFormFields}
                    onFieldMappingsChange={onFieldMappingsChange}
                    onRemove={
                      sessionIndex > 0
                        ? () => {
                            const newMappings = { ...fieldMappings };
                            delete newMappings[`session.${sessionIndex}.customName`];
                            delete newMappings[`session.${sessionIndex}.startTime`];
                            delete newMappings[`session.${sessionIndex}.endTime`];
                            onFieldMappingsChange(newMappings);
                            onGenericSessionCountChange((prev: number) => prev - 1);
                          }
                        : undefined
                    }
                  />
                ))}
                {genericSessionCount < 5 && (
                  <TabletButton
                    variant='outlined'
                    size='small'
                    style={{
                      textTransform: 'none',
                      padding: '6px 16px',
                      borderColor: theme.color.secondary.main900,
                      color: theme.color.secondary.main900,
                    }}
                    onClick={() => onGenericSessionCountChange((prev: number) => prev + 1)}
                  >
                    + Add Session
                  </TabletButton>
                )}
              </>
            )}
          </>
        );

      case 'email':
        return (
          <>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map form fields to Email properties. When sending an Email from the Form Wizard, these values will be
              pre-filled.
            </Typography>
            <TemplatedTextInput
              value={fieldMappings['email.subject'] || ''}
              onChange={(v) => onFieldMappingsChange({ ...fieldMappings, 'email.subject': v })}
              availableFormFields={availableFormFields}
              label='Subject'
              placeholder='e.g. Thanks for your application, {{__submissionNumber}}'
            />
            <RosterNotesTemplateEditor
              value={fieldMappings['email.message'] || ''}
              onChange={(html) => onFieldMappingsChange({ ...fieldMappings, 'email.message': html })}
              availableFormFields={availableFormFields}
              label='Email Message Template'
              placeholder='Compose email message with merge tags...'
            />
            <EmailRoutingFields
              keyPrefix='email'
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
            />
          </>
        );

      case 'calendar_invite':
        return (
          <>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map form fields to Calendar Invite properties. When sending a Calendar Invite from the Form Wizard, these
              values will be pre-filled.
            </Typography>
            <TemplatedTextInput
              value={fieldMappings['calendarInvite.title'] || ''}
              onChange={(v) => onFieldMappingsChange({ ...fieldMappings, 'calendarInvite.title': v })}
              availableFormFields={availableFormFields}
              label='Event Title'
              placeholder='e.g. Interview with {{firstName}} {{lastName}}'
            />
            <Grid container spacing={2}>
              {calendarInviteFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FormControl fullWidth variant='outlined' size='small'>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })}
                      label={field.label}
                    >
                      <MenuItem value=''>
                        <em>No mapping</em>
                      </MenuItem>
                      {availableFormFields.map((formField) => (
                        <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                          {formField.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
            <RosterNotesTemplateEditor
              value={fieldMappings['calendarInvite.notes'] || ''}
              onChange={(html) => onFieldMappingsChange({ ...fieldMappings, 'calendarInvite.notes': html })}
              availableFormFields={availableFormFields}
              label='Calendar Invite Notes Template'
              placeholder='Compose calendar invite notes with merge tags...'
            />
            <EmailRoutingFields
              keyPrefix='calendarInvite'
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              fields={CALENDAR_INVITE_ROUTING_FIELDS}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Form Wizard Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            wizardSettingsExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setWizardSettingsExpanded(!wizardSettingsExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <SettingsIcon style={{ fontSize: '18px' }} /> Form Wizard
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Configure field mappings for the Form Wizard
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {wizardSettingsExpanded && (
              <Box onClick={(e) => e.stopPropagation()}>
                <TabletButton
                  variant='contained'
                  color='primary'
                  onClick={onSaveFieldMappings}
                  disabled={savingMappings}
                  style={{
                    backgroundColor: theme.color.secondary.main900,
                    color: '#ffffff',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    textTransform: 'none',
                    padding: '7px 12px',
                    minWidth: '40px',
                  }}
                >
                  {savingMappings ? 'Saving...' : 'Save'}
                </TabletButton>
              </Box>
            )}
            <IconButton size='small' className={classes.settingsExpandIcon}>
              {wizardSettingsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={wizardSettingsExpanded}>
          <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
            Configure field mappings for each entity type. Use the radio button to set the default pre-selection when
            opening the Form Wizard.
          </Typography>

          <Box style={{ marginBottom: '16px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(fieldMappings['submissionNumbers.enabled'])}
                  onChange={(e) =>
                    onFieldMappingsChange({
                      ...fieldMappings,
                      'submissionNumbers.enabled': e.target.checked ? 'true' : '',
                    })
                  }
                  color='primary'
                />
              }
              label={
                <Box>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
                    Auto-number submissions
                  </Typography>
                  <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>
                    Each new submission to this form gets a sequential number, available as a &quot;Submission
                    Number&quot; option in dropdowns and as the <code>&#123;&#123;__submissionNumber&#125;&#125;</code>{' '}
                    merge tag.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {ENTITY_TYPE_CONFIGS.map((config) => {
            const isDefault = fieldMappings['wizard.defaultEntityType'] === config.type;
            const isExpanded = expandedCards[config.type];

            return (
              <Box key={config.type} className={classes.entityCard}>
                <Box className={classes.entityCardHeader} onClick={() => toggleCard(config.type)}>
                  <Box
                    className={classes.entityCardIconBox}
                    style={{
                      backgroundColor: isDefault ? theme.palette.primary.main : theme.color.grey.neutralBrand100,
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d={config.svgPath} fill={isDefault ? '#fff' : '#646464'} />
                    </svg>
                  </Box>
                  <Box className={classes.entityCardInfo}>
                    <Typography className={classes.entityCardTitle}>{config.title}</Typography>
                    <Typography className={classes.entityCardDescription}>{config.description}</Typography>
                  </Box>
                  <Box className={classes.entityCardActions}>
                    <IconButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        onFieldMappingsChange({
                          ...fieldMappings,
                          'wizard.defaultEntityType': isDefault ? '' : config.type,
                        });
                      }}
                      title={isDefault ? 'Remove as default' : 'Set as default'}
                      style={{ padding: '4px' }}
                    >
                      {isDefault ? (
                        <RadioButtonChecked style={{ fontSize: '20px', color: theme.palette.primary.main }} />
                      ) : (
                        <RadioButtonUnchecked style={{ fontSize: '20px', color: '#b0b0b0' }} />
                      )}
                    </IconButton>
                    <IconButton size='small' className={classes.settingsExpandIcon}>
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={isExpanded}>
                  <Box className={classes.entityCardBody}>{renderEntityMappings(config.type)}</Box>
                </Collapse>
              </Box>
            );
          })}
        </Collapse>
      </Box>

      {form?.requiresApproval && (
        <Box className={classes.settingsSection}>
          <Box
            className={`${classes.settingsSectionHeader} ${
              approvalWorkflowExpanded ? classes.settingsSectionHeaderExpanded : ''
            }`}
            onClick={() => setApprovalWorkflowExpanded(!approvalWorkflowExpanded)}
          >
            <Box className={classes.settingsSectionInfo}>
              <Typography className={classes.settingsSectionTitle}>
                <AssignmentTurnedIn style={{ fontSize: '18px' }} /> Approval Workflow Emails
              </Typography>
              <Typography className={classes.settingsSectionDescription}>
                Automatically email the submitter when a submission is approved or declined
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {approvalWorkflowExpanded && (
                <Box onClick={(e) => e.stopPropagation()}>
                  <TabletButton
                    variant='contained'
                    color='primary'
                    onClick={onSaveFieldMappings}
                    disabled={savingMappings}
                    style={{
                      backgroundColor: theme.color.secondary.main900,
                      color: '#ffffff',
                      boxSizing: 'border-box',
                      borderRadius: '4px',
                      textTransform: 'none',
                      padding: '7px 12px',
                      minWidth: '40px',
                    }}
                  >
                    {savingMappings ? 'Saving...' : 'Save'}
                  </TabletButton>
                </Box>
              )}
              <IconButton size='small' className={classes.settingsExpandIcon}>
                {approvalWorkflowExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={approvalWorkflowExpanded}>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Map a form field that holds the submitter&apos;s email address, then compose subject and body templates
              for each outcome. Leave subject or body empty to skip sending that outcome&apos;s email.
            </Typography>

            {(() => {
              const submitterEmailFields = availableFormFields.filter(
                (field) => field.fieldType === 'VOLUNTEER_EmailTaskFieldType'
              );
              return (
                <FormControl fullWidth variant='outlined' size='small' style={{ marginBottom: '24px' }}>
                  <InputLabel>Submitter Email Field</InputLabel>
                  <Select
                    value={fieldMappings['approvalWorkflow.submitterEmailFieldId'] || ''}
                    onChange={(e) =>
                      onFieldMappingsChange({
                        ...fieldMappings,
                        'approvalWorkflow.submitterEmailFieldId': String(e.target.value),
                      })
                    }
                    label='Submitter Email Field'
                  >
                    <MenuItem value=''>
                      <em>
                        {submitterEmailFields.length === 0
                          ? 'Add an Email field to this form to enable the workflow'
                          : 'No mapping — workflow disabled'}
                      </em>
                    </MenuItem>
                    {submitterEmailFields.map((field) => (
                      <MenuItem key={field.taskFieldId} value={field.taskFieldId}>
                        {field.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            })()}

            <Typography className={classes.sectionTitle} style={{ marginBottom: '12px' }}>
              Approved Email Template
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              label='Subject'
              placeholder='Your submission has been approved'
              value={fieldMappings['approvalWorkflow.approvedSubject'] || ''}
              onChange={(e) =>
                onFieldMappingsChange({
                  ...fieldMappings,
                  'approvalWorkflow.approvedSubject': e.target.value,
                })
              }
              style={{ marginBottom: '8px' }}
            />
            <RosterNotesTemplateEditor
              value={fieldMappings['approvalWorkflow.approvedMessage'] || ''}
              onChange={(html) => onFieldMappingsChange({ ...fieldMappings, 'approvalWorkflow.approvedMessage': html })}
              availableFormFields={availableFormFields}
              label='Approved Message Body'
              placeholder='Compose the email sent when a submission is approved...'
            />

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <Typography className={classes.sectionTitle} style={{ marginBottom: '12px' }}>
              Declined Email Template
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              label='Subject'
              placeholder='Update on your submission'
              value={fieldMappings['approvalWorkflow.declinedSubject'] || ''}
              onChange={(e) =>
                onFieldMappingsChange({
                  ...fieldMappings,
                  'approvalWorkflow.declinedSubject': e.target.value,
                })
              }
              style={{ marginBottom: '8px' }}
            />
            <RosterNotesTemplateEditor
              value={fieldMappings['approvalWorkflow.declinedMessage'] || ''}
              onChange={(html) => onFieldMappingsChange({ ...fieldMappings, 'approvalWorkflow.declinedMessage': html })}
              availableFormFields={availableFormFields}
              label='Declined Message Body'
              placeholder='Compose the email sent when a submission is declined...'
            />

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <Typography className={classes.sectionTitle} style={{ marginBottom: '4px' }}>
              Delivery
            </Typography>
            <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300, marginBottom: '12px' }}>
              Applied to both the approved and declined emails
            </Typography>
            <EmailRoutingFields
              keyPrefix='approvalWorkflow'
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
            />
          </Collapse>
        </Box>
      )}

      {/* Admin-Only Fields Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            adminFieldsExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setAdminFieldsExpanded(!adminFieldsExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <LockIcon style={{ fontSize: '18px' }} /> Admin-Only Fields
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Add internal fields only admins can see and fill in
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {adminFieldsExpanded && (
              <Box onClick={(e) => e.stopPropagation()}>
                <TabletButton
                  variant='contained'
                  color='primary'
                  onClick={handleSaveAdminFieldsClick}
                  disabled={savingAdminFields}
                  style={{
                    backgroundColor: theme.color.secondary.main900,
                    color: '#ffffff',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    textTransform: 'none',
                    padding: '7px 12px',
                    minWidth: '40px',
                  }}
                >
                  {savingAdminFields ? 'Saving...' : 'Save'}
                </TabletButton>
              </Box>
            )}
            <IconButton size='small' className={classes.settingsExpandIcon}>
              {adminFieldsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={adminFieldsExpanded}>
          {draftAdminFields.length === 0 && (
            <Typography style={{ fontSize: '0.85rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              No admin fields yet. Add one below — for example an &quot;Approval Date&quot; date field or a reference
              number.
            </Typography>
          )}
          {draftAdminFields.map((draftField, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <TextField
                variant='outlined'
                size='small'
                label='Field name'
                value={draftField.name}
                onChange={(e) => updateDraftAdminField(index, { name: e.target.value })}
                style={{ flex: 1 }}
              />
              <FormControl variant='outlined' size='small' style={{ width: '140px' }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={draftField.type}
                  label='Type'
                  onChange={(e) => updateDraftAdminField(index, { type: e.target.value as AdminFieldType })}
                >
                  {ADMIN_FIELD_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton size='small' disabled={index === 0} onClick={() => moveDraftAdminField(index, -1)}>
                <ArrowUpward style={{ fontSize: '18px' }} />
              </IconButton>
              <IconButton
                size='small'
                disabled={index === draftAdminFields.length - 1}
                onClick={() => moveDraftAdminField(index, 1)}
              >
                <ArrowDownward style={{ fontSize: '18px' }} />
              </IconButton>
              <IconButton
                size='small'
                onClick={() => setDraftAdminFields((prev) => prev.filter((_unusedField, i) => i !== index))}
              >
                <DeleteIcon style={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
          ))}
          <TabletButton
            variant='outlined'
            startIcon={<AddIcon style={{ fontSize: '16px' }} />}
            onClick={() => setDraftAdminFields((prev) => [...prev, { adminFieldId: null, name: '', type: 'text' }])}
            style={{ textTransform: 'none', borderRadius: '4px' }}
          >
            Add field
          </TabletButton>
        </Collapse>
      </Box>

      {/* Notifications Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            notificationsExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setNotificationsExpanded(!notificationsExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <NotificationsIcon style={{ fontSize: '18px' }} /> Notifications
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Configure notification settings for form submissions
            </Typography>
          </Box>
          <IconButton size='small' className={classes.settingsExpandIcon}>
            {notificationsExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={notificationsExpanded}>
          <FormNotificationSettings
            formId={formId || ''}
            notificationConfig={form?.notificationConfig}
            onSaved={() => {
              onRefetchForms();
            }}
          />
        </Collapse>
      </Box>

      {/* Views Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            individualViewExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setIndividualViewExpanded(!individualViewExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <Visibility style={{ fontSize: '18px' }} /> Views
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Set the defaults for how responses are viewed
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {individualViewExpanded && (
              <Box onClick={(e) => e.stopPropagation()}>
                <TabletButton
                  variant='contained'
                  color='primary'
                  onClick={onSaveFieldMappings}
                  disabled={savingMappings}
                  style={{
                    backgroundColor: theme.color.secondary.main900,
                    color: '#ffffff',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    textTransform: 'none',
                    padding: '7px 12px',
                    minWidth: '40px',
                  }}
                >
                  {savingMappings ? 'Saving...' : 'Save'}
                </TabletButton>
              </Box>
            )}
            <IconButton size='small' className={classes.settingsExpandIcon}>
              {individualViewExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={individualViewExpanded}>
          <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
            These can be altered while viewing
          </Typography>
          <Box style={{ marginBottom: '8px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(fieldMappings['individualView.groupByHeadings'])}
                  onChange={(e) =>
                    onFieldMappingsChange({
                      ...fieldMappings,
                      'individualView.groupByHeadings': e.target.checked ? 'true' : '',
                    })
                  }
                  color='primary'
                />
              }
              label={
                <Box>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
                    Group by section
                  </Typography>
                  <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>
                    Answers sit under the form&apos;s headings
                  </Typography>
                </Box>
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(fieldMappings['individualView.showUnanswered'])}
                  onChange={(e) =>
                    onFieldMappingsChange({
                      ...fieldMappings,
                      'individualView.showUnanswered': e.target.checked ? 'true' : '',
                    })
                  }
                  color='primary'
                />
              }
              label={
                <Box>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
                    Show every question
                  </Typography>
                  <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>
                    Include questions the submitter left blank
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
